import styled, { CSSObject } from '@emotion/styled'
import { InputGroup } from '../Controls'
import { UseFormReturn } from 'react-hook-form'
import { FormFields } from './types'
import AlertMessage from '../Cart/AlertMessage'
import {
  focusNextInput,
  handlePhoneNumberOnChange,
  handlePhoneNumberOnBlur
} from './utils'
import useInputKeyEnterHandler from 'shop/hooks/useInputKeyEnterHandler'
import FormInput from '../Inputs/FormInput'
import { HalfWidthFlexContainer } from './commonStyles'
import { emailRegex } from 'shop/utils/common'
import { IntlPhoneNumber } from '../Inputs'
import { useCheckout } from 'shop/hooks'
import { useEffect, useRef, useState } from 'react'
import { strippedContactNumPrefix } from '../Inputs/utils'
import { CartBreakdownItem } from '../CartV2'
import { AccountIcon } from 'shop/assets/icons'
import ExpandableDiv from '../ExpandableContainer/ExpandableDiv'

const CUSTOMER_PREFIX = 'customerDetails'
export const CUSTOMER_INPUTS = {
  firstNameInputId: `${CUSTOMER_PREFIX}[firstName]`,
  lastNameInputId: `${CUSTOMER_PREFIX}[lastName]`,
  emailInputId: `${CUSTOMER_PREFIX}[email]`,
  phonePrefixId: `${CUSTOMER_PREFIX}[contactNumPrefix]`,
  phoneInputId: `${CUSTOMER_PREFIX}[contactNumber]`
}

type Props = { formHandle: UseFormReturn<FormFields>; onBlur: () => void }

const CustomerDetailsForm = ({ formHandle, onBlur }: Props) => {
  const {
    register,
    formState: { errors },
    watch,
    setValue,
    trigger
  } = formHandle
  const detailsErrors = errors['customerDetails']
  const formInitialised = watch('__formInitialised')

  const [isPersonalDetailsFormOpen, setIsPersonalDetailsFormOpen] =
    useState<boolean>(false)
  const [hasLoaded, setHasLoaded] = useState<boolean>(false)
  const sectionRef = useRef<HTMLDivElement>(null)

  const { trackAddShippingInfo } = useCheckout()

  const {
    firstNameInputId,
    lastNameInputId,
    emailInputId,
    phonePrefixId,
    phoneInputId
  } = CUSTOMER_INPUTS

  const customerDetails = watch('customerDetails')

  const { firstName, lastName, email, contactNumber } = customerDetails
  const hasAllDetails = !!firstName && !!lastName && !!email && !!contactNumber

  const handleBlur = () => {
    onBlur()
    // sending tracking event, only if all fields are filled
    if (hasAllDetails) {
      trackAddShippingInfo()
    }
  }

  useInputKeyEnterHandler({
    handleKeyPress: () =>
      focusNextInput([
        firstNameInputId,
        lastNameInputId,
        emailInputId,
        phoneInputId
      ])
  })

  const handleEmailInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.target.value = e.target.value.toLowerCase()
  }

  const handlePhoneInputsBlur = (
    e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    handlePhoneNumberOnBlur(
      e,
      setValue,
      trigger,
      'customerDetails.contactNumber'
    )
    handleBlur()
  }

  // Expand the form on load if incomplete or with errors
  useEffect(() => {
    if ((detailsErrors || !hasAllDetails) && formInitialised) {
      setIsPersonalDetailsFormOpen(true)
    }
    let timeout: NodeJS.Timeout
    if (formInitialised) {
      timeout = setTimeout(() => {
        setHasLoaded(true)
      }, 600) // wait for ExpandableDiv animation to finish
    }

    return () => {
      if (timeout) clearTimeout(timeout)
    }
  }, [detailsErrors, hasAllDetails, formInitialised])

  // Collapse the form if complete and without errors if another input has been focused
  useEffect(() => {
    const handleFocus = (e: FocusEvent) => {
      const focusedEl = e.target as HTMLElement
      if (!sectionRef.current?.contains(focusedEl)) {
        // another element outside of this section has been focused
        // don't collapse the section if there are errors or missing details
        if (detailsErrors || !hasAllDetails) return
        setIsPersonalDetailsFormOpen(false)
      }
    }
    document.addEventListener('focusin', handleFocus)
    return () => document.removeEventListener('focusin', handleFocus)
  }, [hasAllDetails, detailsErrors])

  if (!formInitialised) return <div style={{ height: '100px' }}></div>

  return (
    <div>
      {detailsErrors && (
        <AlertMessageWrapper>
          <AlertMessage
            skin='mild'
            type='error'
            heading={Object.values(detailsErrors as object)[0]?.message || ''}
          />
        </AlertMessageWrapper>
      )}
      <ExpandableDiv
        isExpanded={!isPersonalDetailsFormOpen && hasAllDetails}
        collapsedHeight={0}
        transition={hasLoaded ? undefined : 'none'}
      >
        <CollapsedDetailsContainer>
          <CartBreakdownItem
            label={
              <CollapsedDetails>
                <span>
                  {customerDetails.firstName} {customerDetails.lastName}
                </span>
                <span>
                  <span>{customerDetails.email}</span>
                  <span>{'•'}</span>
                  <span>
                    {strippedContactNumPrefix(customerDetails.contactNumPrefix)}
                    {customerDetails.contactNumber}
                  </span>
                </span>
              </CollapsedDetails>
            }
            maxWidth={'100%'}
            withEllipsis={false}
            withLineBreak={false}
            LeftIcon={StyledAccountIcon}
            padding={'8px 0 8px 0'}
            onChangeButtonClick={() =>
              setIsPersonalDetailsFormOpen(!isPersonalDetailsFormOpen)
            }
            changeButtonAlign={'flex-start'}
          />
        </CollapsedDetailsContainer>
      </ExpandableDiv>
      <ExpandableDiv
        isExpanded={isPersonalDetailsFormOpen}
        collapsedHeight={0}
        transition={hasLoaded ? undefined : 'none'}
        id='customer-details-expandable-div'
      >
        <FieldsContainer ref={sectionRef}>
          <InputGroup templateColumns='1fr' gridGap='24px'>
            <HalfWidthFlexContainer>
              <FormInput
                id={firstNameInputId}
                labelText='First Name'
                name={firstNameInputId}
                autoComplete='given-name'
                value={customerDetails?.firstName || ''}
                onBlur={handleBlur}
                showError={!!errors?.customerDetails?.firstName}
                formRef={register('customerDetails.firstName', {
                  required: 'Please enter your first name',
                  validate: (value: string) =>
                    value.trim().length > 0
                      ? true
                      : 'Please enter your first name'
                })}
                scrollSettings={{
                  scrollOnFocus: true,
                  scrollToElementId: 'checkout-form-wrapper'
                }}
              />
              <FormInput
                id={lastNameInputId}
                name={lastNameInputId}
                value={customerDetails?.lastName || ''}
                labelText='Last Name'
                autoComplete='family-name'
                onBlur={handleBlur}
                showError={!!errors?.customerDetails?.lastName}
                formRef={register('customerDetails.lastName', {
                  required: 'Please enter your last name',
                  validate: (value: string) =>
                    value.trim().length > 0
                      ? true
                      : 'Please enter your last name'
                })}
                scrollSettings={{
                  scrollOnFocus: true,
                  scrollToElementId: 'checkout-form-wrapper'
                }}
              />
            </HalfWidthFlexContainer>
            <FormInput
              id={emailInputId}
              name={emailInputId}
              labelText='Email Address'
              value={customerDetails?.email || ''}
              autoComplete='email'
              onBlur={handleBlur}
              onChange={handleEmailInputChange}
              showError={!!errors?.customerDetails?.email}
              inputMode='email'
              formRef={register('customerDetails.email', {
                required: 'Please enter your email address',
                pattern: {
                  value: emailRegex,
                  message: 'Invalid email address'
                }
              })}
              scrollSettings={{
                scrollOnFocus: true,
                scrollToElementId: 'checkout-form-wrapper'
              }}
            />
            <IntlPhoneNumber
              onNumberChange={handlePhoneNumberOnChange}
              onBlur={handlePhoneInputsBlur}
              formHandle={formHandle}
              prefixDropdownId={phonePrefixId}
              labelText='Phone Number'
              prefixDropdownValue={customerDetails?.contactNumPrefix}
              prefixDropdownName={'customerDetails.contactNumPrefix'}
              phoneNumberInputName={'customerDetails.contactNumber'}
              phoneNumberInputId={phoneInputId}
              phoneNumberValue={customerDetails?.contactNumber || ''}
              showError={
                !!errors?.customerDetails?.contactNumber ||
                !!errors?.customerDetails?.contactNumPrefix
              }
              scrollSettings={{
                scrollOnFocus: true,
                scrollToElementId: 'checkout-form-wrapper'
              }}
              forceCloseDropdown={!isPersonalDetailsFormOpen}
            />
          </InputGroup>
        </FieldsContainer>
      </ExpandableDiv>
    </div>
  )
}

const AlertMessageWrapper = styled.div(() => ({
  margin: '24px 0 0',
  '>div': {
    marginBottom: '0'
  }
}))

const FieldsContainer = styled.div(
  (): CSSObject => ({
    display: 'block',
    padding: '24px 0'
  })
)

const CollapsedDetailsContainer = styled.div(
  (): CSSObject => ({
    marginBottom: '8px'
  })
)

const CollapsedDetails = styled.span(
  ({ theme }): CSSObject => ({
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',

    '& span:first-of-type': {
      fontWeight: 500
    },
    // email and phone number
    '& span:nth-of-type(2)': {
      display: 'flex',
      flexDirection: 'column',
      gap: '2px',

      // the "•" value
      '& span:nth-of-type(2)': {
        display: 'none'
      }
    },

    [theme.mediaQueries.viewport7]: {
      // email and phone number
      '& span:nth-of-type(2)': {
        flexDirection: 'row',
        gap: '4px',

        // the "•" value
        '& span:nth-of-type(2)': {
          display: 'initial'
        }
      }
    }
  })
)

const StyledAccountIcon = styled(AccountIcon)(() => ({
  '>path': {
    fill: '#000'
  }
}))

export default CustomerDetailsForm
